import { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { getLtcPool } from '@shared/calculations/funding/projection/projectionTotals';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { appliedCapTooltipInputs, InflationTooltipContent } from '../cells';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import { indemnityPayoutWaterfall } from './indemnityPayout';
import { reimbursementPayoutRows } from './payoutBreakdown';
import type { StrategyPresentation } from './types';

// Traditional / standalone LTC (generic + CareScout Care Assurance). A single
// coverage pool drawn by reimbursement (or cash indemnity), no second pool and
// no death benefit. The two per-cell tooltips below explain the "Projected
// payout" and "Pool remaining" columns, which otherwise had no hover for this
// product family. All values are read off the precomputed month state/deltas —
// the presentation never re-derives strategy math.

// "Projected payout" cell tooltip — reimbursement (or indemnity) waterfall
// from care cost to policy paid to out-of-pocket, for single-pool products
// (no AOB/COB split line). Shared by traditional LTC and STCI:
// `benefitPeriodStrandedDays` (STCI only) is the count of gate-eligible days
// the lifetime benefit-period cap stranded this row — they surface as their
// own out-of-pocket line, and keep the tooltip alive on rows where the period
// clamped every payable day away (`coverageEligibleDays` is already the
// engine's period-clamped payable count). `indemnityModeText` and
// `hidePoolDrawnFooter` (ManhattanLife OmniFlex only) exist because that
// product has no dollar pool at all — the default "drawn from the LTC pool"
// intro and footer would be false there; every pool-based caller leaves them
// unset.
export function singlePoolPayoutCellTooltip({
  month,
  terms,
  benefitPeriodStrandedDays = 0,
  indemnityModeText,
  hidePoolDrawnFooter = false,
}: {
  month: MonthlyEntry;
  terms: ClaimTerms;
  benefitPeriodStrandedDays?: number;
  indemnityModeText?: string;
  hidePoolDrawnFooter?: boolean;
}): ReactNode | null {
  const eligibleDays = month.facts.coverageEligibleDays;
  // Keep the tooltip alive when room & board is still out-of-pocket on a
  // fully-ineligible month — Independent Living books R&B even on non-paying
  // months (waiting period / <2 ADL), and it's the exact OOP this modeling
  // exists to surface (cmTwo guards the same way). Otherwise there's nothing to
  // break down and PaidCell shows the zero-row reason natively.
  if (
    eligibleDays === 0 &&
    benefitPeriodStrandedDays === 0 &&
    (month.facts.roomAndBoardCost ?? 0) === 0
  ) {
    return null;
  }

  const drawn = month.deltas.base.depletion;

  const isIndemnity = terms.isIndemnity;
  // Eligible cost the policy didn't cover. With pool still open it was the
  // monthly cap that bound; with the pool closed at $0 it was exhaustion.
  const poolExhausted = getLtcPool(month.state) === 0;
  const oopPeriodStranded = benefitPeriodStrandedDays * month.facts.dailyCareCost;

  const mode = isIndemnity ? 'indemnity' : 'reimbursement';
  const modeText = isIndemnity
    ? indemnityModeText ??
      'Pays a fixed cash benefit up to the monthly cap regardless of actual cost, drawn from the LTC pool. Cash above the eligible cost banks as a surplus reserve, drained to cover cost on later shortfall months.'
    : 'Reimburses eligible incurred cost, up to the monthly cap, drawn from the LTC pool.';

  // STCI only: when the lifetime benefit period clamps the row, the indemnity
  // cash benefit is pro-rated to the payable days left (cap × payableDays/30),
  // so annotate the cash line with those days. `benefitPeriodStrandedDays` is
  // only non-zero for STCI (traditional LTC / membership pass the default 0),
  // so this note never shows for products without a benefit-period cap.
  const benefitPeriodNote =
    benefitPeriodStrandedDays > 0
      ? eligibleDays === 0
        ? 'benefit period exhausted'
        : `${eligibleDays}d left in benefit period`
      : undefined;

  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">Projected payout — {mode}</div>
      <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        {isIndemnity ? (
          // Indemnity is a cash waterfall (cash → applied to cost → net OOP),
          // not a day-bucket breakdown — see `indemnityPayoutWaterfall`.
          indemnityPayoutWaterfall(month, benefitPeriodNote)
        ) : (
          reimbursementPayoutRows({
            month,
            overCapLabel: poolExhausted ? '· benefit pool used up' : '· over monthly cap',
            extraOopReasons:
              oopPeriodStranded > 0
                ? [
                    {
                      label: (
                        <>
                          · benefit period exhausted
                          <span className="text-gray-600"> · {benefitPeriodStrandedDays}d</span>
                        </>
                      ),
                      value: oopPeriodStranded,
                    },
                  ]
                : [],
          })
        )}
      </div>
      {!hidePoolDrawnFooter && (
        <div className="mt-1 text-[10px] text-gray-400">
          Drawn from LTC pool {currency(drawn)}
        </div>
      )}
    </TooltipContent>
  );
}

export const traditionalLtcPresentation: StrategyPresentation = {
  // "Benefit cap" cell — headline the engine's ACTUAL applied monthly cap
  // (`month.facts.appliedMonthlyBenefitCap`), not the year-snapshot's
  // `monthlyBenefitCap`. The snapshot cap is the MAX daily benefit across care
  // environments × 30, but the engine applies the cap for the ROW's specific
  // care environment (e.g. a lower home-care daily benefit). Without this
  // override the column falls back to the snapshot cap, which can read HIGHER
  // than what actually bound the adjacent payout, so the "over monthly cap"
  // line in the payout tooltip wouldn't reconcile. Mirrors oaAssetCare.
  benefitAmountCell: ({ month, terms }) => {
    // Pre-policy rows carry no applied cap; fall through to the default cell
    // (which renders a dash when there's no snapshot for the year).
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;
    const { baseMonthly, rate, capYears } = appliedCapTooltipInputs(month, terms);
    const intYear = Math.floor(month.context.policyYear);
    const cap = capYears && capYears > 0 ? capYears : null;
    // Exponent = policyYear − 1, capped at capYears, matching the engine's
    // `poolGrowthFactor`/`compoundFactor` so the tooltip's base × (1+r)^n
    // equals the displayed result. Simple riders grow linearly instead.
    const isSimple = terms.inflationIsSimple === true;
    const rawIncrements = Math.max(intYear - 1, 0);
    const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
    const capped = cap !== null && intYear > cap;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <InflationTooltipContent
          header={`Policy year ${intYear} · monthly maximum`}
          startingLabel={`At issue (${terms.policyPremiumStartYear})`}
          startingValue={baseMonthly}
          rate={rate}
          years={increments}
          result={monthlyCap}
          note={capped ? `Inflation capped at year ${cap}` : undefined}
          isSimple={isSimple}
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  // "Pool remaining" cell — single-pool open → close breakdown. Traditional
  // LTC has one coverage pool (no AOB/COB split, no death benefit), and its
  // inflation rider grows the *remaining* balance at each policy anniversary.
  poolCellTooltip: ({ month, openState, terms }) => {
    // Unlimited pools have no meaningful running balance (the sentinel drifts
    // down by draws), so the open→close breakdown would show a misleading ~$1B.
    // Explain the unlimited coverage instead.
    if (terms.isUnlimited) {
      return (
        <TooltipContent className="max-w-xs text-xs">
          <div className="mb-1 font-semibold">LTC coverage pool — Unlimited</div>
          <div className="text-[10px] text-gray-300">
            This policy has no lifetime maximum, so the coverage pool never depletes. Each month
            still pays up to the monthly maximum; any care cost above that cap is out of pocket.
          </div>
        </TooltipContent>
      );
    }

    const opening = getLtcPool(openState);
    const closing = getLtcPool(month.state);
    const accrual = month.deltas.base.inflationAccrual;
    const drawn = month.deltas.base.depletion;

    if (opening === 0 && closing === 0 && accrual === 0 && drawn === 0) return null;

    // Joint ShareCare rows surface only the active claimant's accessible
    // capacity (their ⅓ individual pool + the ⅓ shared pool), while
    // year-level surfaces show the full policy ceiling — state the ⅔ vs 3/3
    // relationship so the gap doesn't read as missing money (P2-23). Claimant
    // tags only exist on joint rows, so single-life copy is unchanged. The
    // `jointPools` sub-pools exist only on the ShareCare products (not generic
    // joint), so they gate the shared-pool usage block — on a ShareCare row we
    // also surface how much of the common ⅓ pool has been consumed across BOTH
    // spouses (`sharedPoolDrawnToDate` is an explicit engine running total, not
    // an `initial − remaining` inference, since the shared pool inflates).
    const shareCareState = month.state.strategyState as
      | { jointPools?: { shared: number }; sharedPoolDrawnToDate?: number }
      | undefined;
    const sharedPool = shareCareState?.jointPools;
    const jointShareCareNote =
      month.context.claimant == null ? undefined : sharedPool ? (
        <div className="space-y-1">
          <div>Policy ceiling covers both spouses (⅓ each + ⅓ shared); each claimant can access ⅔.</div>
          <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 border-t border-gray-700 pt-1 text-gray-300">
            <div className="col-span-2 text-[10px] uppercase tracking-wide text-gray-400">
              Shared ⅓ pool (both spouses)
            </div>
            <span>Remaining</span>
            <span className="font-semibold tabular-nums text-gray-100">
              {currency(sharedPool.shared)}
            </span>
            <span>Drawn to date</span>
            <span className="font-semibold tabular-nums text-gray-100">
              {currency(shareCareState?.sharedPoolDrawnToDate ?? 0)}
            </span>
          </div>
        </div>
      ) : undefined;

    return (
      <BalanceBreakdownTooltip
        title="LTC coverage pool"
        description="Traditional LTC reimburses care from a single coverage pool. Unused daily benefit stays in the pool; with an inflation rider, the remaining balance grows at each policy anniversary."
        sections={[
          {
            opening,
            closing,
            lines: [
              { label: 'Inflation accrual', value: accrual, kind: 'add' },
              { label: 'Drawn this month', value: drawn, kind: 'subtract' },
            ],
          },
        ]}
        footer={jointShareCareNote}
      />
    );
  },

  // "Projected payout" cell — reimbursement (or indemnity) waterfall from care
  // cost to policy paid to out-of-pocket. Single-pool draw, so there is no
  // AOB/COB split line.
  payoutCellTooltip: ({ month, terms }) => singlePoolPayoutCellTooltip({ month, terms }),

  // Traditional / standalone LTC has no death benefit, and its single coverage
  // pool inflates the *remaining* balance each anniversary — a "theoretical
  // inflated ceiling assuming no draws" doesn't model anything meaningful for
  // this product once any dollar has been drawn, so we hide that column too.
  hiddenColumns: ['pool-limit', 'death-benefit'],

  // Single coverage pool — drop the AOB/COB framing in the Drawn cell.
  drawnCellLabels: {
    description:
      'Cumulative dollars drawn from the LTC coverage pool. ' +
      'Drawn = Σ (monthly pool depletion). ' +
      'Traditional LTC inflates the remaining balance each anniversary, so the pool itself can keep growing — this column counts only what has been paid out.',
    baseLabel: 'Coverage pool drawn',
    singlePool: true,
  },

  copy: {
    // Simple riders step on a FIXED at-issue base each anniversary; compound
    // riders grow the remaining balance. The accrual formula must match the
    // engine's branch or the rows visibly contradict it (P2-24).
    poolTooltip: terms => (
      <HeaderTooltip
        description="End-of-month LTC coverage pool balance, with this month's change beneath. Traditional LTC reimburses from a single coverage pool; with an inflation rider the remaining balance grows at each policy anniversary."
        formulas={[
          { label: 'Closing pool', expression: 'opening + inflation accrual − drawn this month' },
          {
            label: 'Inflation accrual',
            expression:
              terms.inflationIsSimple === true
                ? 'at-issue pool × inflation rate (a fixed step at each policy anniversary)'
                : 'opening × inflation rate (at each policy anniversary)',
          },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month, with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          { label: 'Reimbursement mode', expression: 'min(eligible cost, monthly maximum, pool remaining)' },
          { label: 'Indemnity mode', expression: 'min(monthly maximum, pool remaining)' },
        ]}
        notes="Eligible cost excludes waiting-period days and days the insured fails the 2-ADL trigger."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost × <GrowthFactor rate="care-cost inflation" exponent="years from today" />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this policy year, with the daily equivalent beneath."
        formulas={[
          {
            label: 'Monthly max',
            expression: (
              <>
                at-issue daily × days in month × <GrowthFactor rate="rider inflation rate" exponent="policy year − 1" />
              </>
            ),
          },
        ]}
        notes="Capped at the rider's inflation cap year if specified. Simple-interest riders use (1 + rate × years) instead of compounding."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative dollars drawn from the LTC coverage pool through this month."
        formulas={[
          { label: 'Drawn', expression: 'Σ (monthly pool depletion)' },
        ]}
        notes="Traditional LTC inflates the remaining balance each anniversary, so the pool itself can keep growing — this column counts only what has been paid out, not a ceiling."
      />
    ),
  },
};
